import { useMemo } from "react";

export interface EntityDimensionValue {
  key: string;
  label: string;
  description?: string;
}

export interface EntityDimension {
  code: string;
  name: string;
  question: string;
  required: boolean;
  values?: Record<string, string>;
  default_values?: Record<string, string>;
}

export interface EntityDimensionsIndex {
  version: string;
  dimensions: Record<string, EntityDimension>;
}

function normalizeIndex(raw: any): EntityDimensionsIndex {
  return {
    version: raw?.version ?? "1.0",
    dimensions: raw?.dimensions ?? {},
  };
}

/**
 * Hook that provides the entity dimension index to the entity wizard.
 *
 * The index must be passed in from `useIndexDimensions().indexes.entity_index_dimensions`
 * (DB kernel, served cache-first). There is no bundled reference file to fall back on —
 * before the index has loaded this yields an empty `dimensions` map, which callers
 * treat as a loading state.
 */
export function useEntityDimensions(
  externalIndex?: EntityDimensionsIndex | Record<string, unknown> | null,
): {
  index: EntityDimensionsIndex;
  getDimension: (code: string) => EntityDimension | undefined;
  getDimensionValues: (code: string) => EntityDimensionValue[];
} {
  const index = useMemo<EntityDimensionsIndex>(() => normalizeIndex(externalIndex), [externalIndex]);

  const getDimension = (code: string): EntityDimension | undefined => {
    const dim = index.dimensions[code];
    if (!dim) return undefined;
    return { ...dim, code };
  };

  const getDimensionValues = (code: string): EntityDimensionValue[] => {
    const dim = index.dimensions[code];
    if (!dim) return [];
    const source = dim.values ?? dim.default_values ?? {};
    return Object.entries(source).map(([key, label]) => ({
      key,
      label: typeof label === "string" ? label : String(label),
    }));
  };

  return { index, getDimension, getDimensionValues };
}
